import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useDispatch } from "react-redux";
import { useReelRefs } from "./ReelRefsContext";
import { useAppActivity } from "./AppActivityContext";
import { Settings, GAME_LAYOUT } from "../settings";
import store from "../State/Store/store";
import { soundManager } from "../SoundManager";
import { SOUND_NAME } from "../sounds";
import { symbolData } from "../SymbolData";
import {
  rgsPlay,
  syncBalance,
  setFreeSpinSpinning,
  setFreeSpinComplete,
  setFreeSpinCycleComplete,
  setFreeSpinCoverStarted,
  setShowFreeSpinOutro,
  setShowAdditionalFreeSpins,
  setFreeSpinTriggered,
  resetFreeSpinState,
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setCriticalAnimationPlaying,
  setSpinComplete,
  setFeatureSpinActive,
  openConnectionPopup,
  closeConnectionPopup,
} from "../State/actions";
import { FEATURES_CONFIG } from "../featuresConfig";
import { useInitialIdleAnimations } from "../Hooks/useLandingAnimations";

// Connection popup delay for free spin API calls (ms)
// Slightly longer than base game since there's no reel animation covering the wait
const FREE_SPIN_CONNECTION_TIMEOUT = 2000;

// Diagonal cascade delay between waves (ms)
const DIAGONAL_REVEAL_DELAY = 180;
const DIAGONAL_COVER_DELAY = 115;

/**
 * FreeSpinControlContext
 *
 * 1 click = 1 round:
 *   API → diagonal reveal (with spine landing animations) → done
 *
 * Symbols start covered (masks show flip_idle from scene transition).
 * Reveal uses a diagonal cascade: top-left → bottom-right.
 * When each mask reaches ~70% revealed (sym_reveal event), the symbol's
 * overlay spine landing/idle animation fires and the underlying texture hides.
 */
export const FreeSpinControlContext = createContext(null);

/**
 * Pre-build diagonal wave groups for the grid.
 * Each wave contains all cells where (reel + row) equals the wave index.
 *
 * For a 5×3 grid:
 *   Wave 0: [0,0]
 *   Wave 1: [1,0] [0,1]
 *   Wave 2: [2,0] [1,1] [0,2]
 *   Wave 3: [3,0] [2,1] [1,2]
 *   Wave 4: [4,0] [3,1] [2,2]
 *   Wave 5: [4,1] [3,2]
 *   Wave 6: [4,2]
 */
const buildDiagonalWaves = () => {
  const reelCount = Settings.REEL_COUNT;
  const rowCount = GAME_LAYOUT.SYMBOLS_VISIBLE;
  const maxWave = reelCount - 1 + (rowCount - 1);
  const waves = [];

  for (let wave = 0; wave <= maxWave; wave++) {
    const cells = [];
    for (let reel = 0; reel < reelCount; reel++) {
      const row = wave - reel;
      if (row >= 0 && row < rowCount) {
        cells.push({ reel, row });
      }
    }
    waves.push(cells);
  }

  return waves;
};

const DIAGONAL_WAVES = buildDiagonalWaves();
const DIAGONAL_WAVES_REVERSED = [...DIAGONAL_WAVES].reverse();

export const FreeSpinControlProvider = ({ children }) => {
  const { maskReelRefs, freeSpinReelRefs, freeSpinOverlayReelRefs, dimReelSetRef, winLinesContainerRef, lineWinTextRef, globalLineWinTextRef } =
    useReelRefs();
  const dispatch = useDispatch();
  const spinningRef = useRef(false);
  const connectionTimeoutRef = useRef(null);
  const activeIntervalsRef = useRef([]);
  const cascadeTimeoutsRef = useRef([]);
  const { triggerInitialIdleAnimations } = useInitialIdleAnimations();
  const { isActive } = useAppActivity();
  const isActiveRef = useRef(true);

  // Sync isActive to ref for use in async functions
  useEffect(() => {
    isActiveRef.current = isActive;
  }, [isActive]);

  /**
   * Wait for the tab to become active again.
   * Resolves immediately if already active.
   * Used to gate reveals and round advancement while user is away.
   */
  const waitForActive = useCallback(() => {
    if (isActiveRef.current) return Promise.resolve();
    return new Promise((resolve) => {
      const check = setInterval(() => {
        if (isActiveRef.current) {
          clearInterval(check);
          activeIntervalsRef.current = activeIntervalsRef.current.filter(id => id !== check);
          resolve();
        }
      }, 100);
      activeIntervalsRef.current.push(check);
    });
  }, []);

  /**
   * Clear all tracked intervals and cascade timeouts.
   * Called on unmount and when free spins deactivate.
   */
  const clearPendingTimers = useCallback(() => {
    for (const id of activeIntervalsRef.current) clearInterval(id);
    activeIntervalsRef.current = [];
    for (const id of cascadeTimeoutsRef.current) clearTimeout(id);
    cascadeTimeoutsRef.current = [];
  }, []);

  /**
   * Lightweight cleanup: remove dim, win lines, and win text.
   * Called after all masks have covered — per-symbol overlay cleanup
   * is handled by cleanupSymbol inside coverAllSymbolsDiagonal.
   */
  const cleanupWinEffects = useCallback(() => {
    // Undim reels
    if (dimReelSetRef.current) {
      dimReelSetRef.current.undimReels();
    }

    // Hide win lines
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    // Hide win text
    if (lineWinTextRef?.current) lineWinTextRef.current.hide();
    if (globalLineWinTextRef?.current) globalLineWinTextRef.current.hide();
  }, [dimReelSetRef, winLinesContainerRef, lineWinTextRef, globalLineWinTextRef]);

  /**
   * Trigger landing/idle spine animation and hide the static texture
   * for a single symbol at (reelIndex, rowIndex).
   *
   * Same pattern as useLandingAnimations but for free spin reels:
   * 1. Show overlay spine landing animation
   * 2. Next frame: hide the static sprite texture underneath
   */
  const triggerSymbolRevealAnimation = useCallback(
    (reelIndex, rowIndex) => {
      const overlayReelRef = freeSpinOverlayReelRefs.current?.[reelIndex];
      const overlayController =
        overlayReelRef?.getFreeSpinOverlayReelController?.();

      const freeSpinReelRef = freeSpinReelRefs.current?.[reelIndex];
      const reelController = freeSpinReelRef?.getFreeSpinReelController?.();

      if (!overlayController || !reelController) return;

      // Get the symbol ID from the current round data
      const state = store.getState();
      const reelData = state.game.currentRound?.reels?.[reelIndex];
      const symbolId = reelData?.[rowIndex];

      if (symbolId === undefined) return;

      // Play wild/scatter sound on reveal (same pattern as base game ReelControllerV2)
      const symInfo = symbolData[symbolId];
      if (symInfo) {
        if (symInfo.symbolPay === "scatter") {
          soundManager.play(SOUND_NAME.scatter_land);
        } else if (symInfo.symbolPay === "wild") {
          soundManager.play(SOUND_NAME.wild);
        }
      }

      // Show landing/idle animation on the overlay
      const { hasLanding, hasIdle } = overlayController.getAvailableAnimations(
        rowIndex,
        symbolId,
      );

      if (hasLanding || hasIdle) {
        overlayController.showLandingAnimation(rowIndex, symbolId);

        // Next frame: hide the static texture underneath
        requestAnimationFrame(() => {
          if (reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(rowIndex);
          }
        });
      }
    },
    [freeSpinOverlayReelRefs, freeSpinReelRefs],
  );

  /**
   * Reveal all symbols at once (no cascade — fallback / skip mode).
   */
  const revealAllSymbols = useCallback(() => {
    soundManager.play(SOUND_NAME.free_spin_reveal);
    const promises = [];
    for (let i = 0; i < Settings.REEL_COUNT; i++) {
      const controller = maskReelRefs.current?.[i]?.getMaskReelController?.();
      if (controller) promises.push(controller.revealSymbols());
    }
    return Promise.all(promises);
  }, [maskReelRefs]);

  /**
   * Reveal symbols in a diagonal cascade (top-left → bottom-right).
   * Each wave fires simultaneously, then waits DIAGONAL_REVEAL_DELAY before the next.
   *
   * Two spine events per mask:
   *   - sym_reveal (~80%): triggers landing/idle animation on the symbol
   *   - sym_reveal_complete (~90%): on the last wave, signals spin is complete
   *
   * Returns { lastRevealed, allComplete }:
   *   - lastRevealed: resolves when last wave's last symbol fires sym_reveal_complete
   *   - allComplete: resolves when every flip_out animation has fully completed
   */
  const revealAllSymbolsDiagonal = useCallback(() => {
    soundManager.play(SOUND_NAME.free_spin_reveal);
    const allAnimationPromises = [];
    const lastWaveIndex = DIAGONAL_WAVES.length - 1;
    const lastWaveState = { count: 0, resolve: null };
    const lastRevealed = new Promise((r) => {
      lastWaveState.resolve = r;
    });
    const lastWaveSize = DIAGONAL_WAVES[lastWaveIndex]?.length || 0;

    for (let waveIndex = 0; waveIndex < DIAGONAL_WAVES.length; waveIndex++) {
      const wave = DIAGONAL_WAVES[waveIndex];
      const delay = waveIndex * DIAGONAL_REVEAL_DELAY;
      const isLastWave = waveIndex === lastWaveIndex;

      for (const { reel, row } of wave) {
        const promise = new Promise((resolveCell) => {
          const timeoutId = setTimeout(() => {
            const maskReel = maskReelRefs.current?.[reel];
            if (maskReel?.revealSymbolAt) {
              maskReel
                .revealSymbolAt(
                  row,
                  // onReveal (sym_reveal ~80%) — start landing animation
                  () => triggerSymbolRevealAnimation(reel, row),
                  // onRevealComplete (sym_reveal_complete ~90%) — signal spin complete
                  isLastWave
                    ? () => {
                        lastWaveState.count++;
                        if (lastWaveState.count >= lastWaveSize) {
                          lastWaveState.resolve();
                        }
                      }
                    : null,
                )
                .then(resolveCell);
            } else {
              resolveCell();
            }
          }, delay);
          cascadeTimeoutsRef.current.push(timeoutId);
        });
        allAnimationPromises.push(promise);
      }
    }

    const allComplete = Promise.all(allAnimationPromises);
    return { lastRevealed, allComplete };
  }, [maskReelRefs, triggerSymbolRevealAnimation]);

  /**
   * Cover all symbols (flip_in → flip_idle).
   * Arms the button by setting freeSpinComplete = false.
   * Used by FreeSpinSceneTransition at its midpoint.
   */
  const coverAllSymbols = useCallback(async () => {
    soundManager.play(SOUND_NAME.free_spin_cover);
    const promises = [];
    for (let i = 0; i < Settings.REEL_COUNT; i++) {
      const controller = maskReelRefs.current?.[i]?.getMaskReelController?.();
      if (controller) promises.push(controller.coverSymbols());
    }
    await Promise.all(promises);

    // Arm the button — round is ready
    dispatch(setFreeSpinComplete(false));
  }, [maskReelRefs, dispatch]);

  /**
   * Cover symbols in a diagonal cascade (bottom-right → top-left).
   * Reverse of the reveal direction — same timing, same delay.
   *
   * @param {Function|null} onSymbolCovered - Called per-symbol when its mask covers it: (reel, row)
   *
   * Returns { firstCovered, allComplete }:
   *   - firstCovered: resolves when first wave's last symbol fires sym_cover_complete
   *   - allComplete: resolves when every flip_in animation has fully completed
   */
  const coverAllSymbolsDiagonal = useCallback(
    (onSymbolCovered = null) => {
      soundManager.play(SOUND_NAME.free_spin_cover);
      const allAnimationPromises = [];
      const firstCoverState = { count: 0, resolve: null };
      const firstCovered = new Promise((r) => {
        firstCoverState.resolve = r;
      });
      const firstWaveSize = DIAGONAL_WAVES_REVERSED[0]?.length || 0;

      for (
        let waveIndex = 0;
        waveIndex < DIAGONAL_WAVES_REVERSED.length;
        waveIndex++
      ) {
        const wave = DIAGONAL_WAVES_REVERSED[waveIndex];
        const delay = waveIndex * DIAGONAL_COVER_DELAY;
        const isFirstWave = waveIndex === 0;

        for (const { reel, row } of wave) {
          const promise = new Promise((resolveCell) => {
            const timeoutId = setTimeout(() => {
              const maskReel = maskReelRefs.current?.[reel];
              if (maskReel?.coverSymbolAt) {
                maskReel
                  .coverSymbolAt(
                    row,
                    // onCoverComplete (sym_cover_complete ~25%) —
                    // cleanup spine immediately + track first wave
                    () => {
                      if (onSymbolCovered) onSymbolCovered(reel, row);
                      if (isFirstWave) {
                        firstCoverState.count++;
                        if (firstCoverState.count >= firstWaveSize) {
                          firstCoverState.resolve();
                        }
                      }
                    },
                  )
                  .then(resolveCell);
              } else {
                resolveCell();
              }
            }, delay);
            cascadeTimeoutsRef.current.push(timeoutId);
          });
          allAnimationPromises.push(promise);
        }
      }

      const allComplete = Promise.all(allAnimationPromises).then(() => {
        dispatch(setFreeSpinComplete(false));
      });
      return { firstCovered, allComplete };
    },
    [maskReelRefs, dispatch],
  );

  /**
   * handleFreeSpinSpin — one round.
   * API → diagonal reveal → (no win? wait 1 idle loop → cover) → done
   * Returns true if the loop should continue, false if it's the last round.
   */
  const handleFreeSpinSpin = useCallback(async () => {
    // Block if already in progress
    if (spinningRef.current) return false;

    // Block if free spins not active
    const state = store.getState();
    if (!state.featureGame.freeSpinActive) return false;

    spinningRef.current = true;
    dispatch(setFreeSpinSpinning(true));

    // Mark as feature spin so rgsPlay.pending skips base game isSpinning/spinComplete
    dispatch(setFeatureSpinActive(true));

    // 1. API call — with connection popup timeout for slow responses
    connectionTimeoutRef.current = setTimeout(() => {
      const uiState = store.getState();
      if (!uiState.userInterface.connectionPopupActive) {
        dispatch(openConnectionPopup());
      }
    }, FREE_SPIN_CONNECTION_TIMEOUT);

    try {
      await dispatch(rgsPlay()).unwrap();
    } catch {
      // Error — clear timeout, close popup, bail out
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      const uiState = store.getState();
      if (uiState.userInterface.connectionPopupActive) {
        dispatch(closeConnectionPopup());
      }
      spinningRef.current = false;
      dispatch(setFreeSpinSpinning(false));
      dispatch(setFreeSpinComplete(true));
      dispatch(setFeatureSpinActive(false));
      return false;
    }

    // API responded — clear connection timeout and close popup if shown
    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
    const uiState = store.getState();
    if (uiState.userInterface.connectionPopupActive) {
      dispatch(closeConnectionPopup());
    }

    // Feature spin done — clear flag
    dispatch(setFeatureSpinActive(false));

    // 2. Let React update FreeSpinReel with new data
    await new Promise((r) => requestAnimationFrame(r));

    // 3. Wait for user to be on the tab before revealing
    //    If they left during the API call, don't reveal to an empty tab
    await waitForActive();

    // 4. Diagonal reveal — start cascade, wait for last symbol to be visually revealed
    const revealStartTime = Date.now();
    const { lastRevealed } = revealAllSymbolsDiagonal();
    await lastRevealed;

    // 4. Check for win
    const roundData = store.getState().game.currentRound;
    const hasWin = roundData?.win && roundData.win > 0;

    // Per-symbol cleanup: kill overlay spine + restore texture as each mask covers
    const cleanupSymbol = (reel, row) => {
      const overlayRef = freeSpinOverlayReelRefs.current?.[reel];
      const overlayCtrl = overlayRef?.getFreeSpinOverlayReelController();
      if (overlayCtrl) {
        overlayCtrl.hideAnimationAt?.(row);
      }

      const reelRef = freeSpinReelRefs.current?.[reel];
      const reelCtrl = reelRef?.getFreeSpinReelController();
      if (reelCtrl) {
        reelCtrl.showSymbolTexture?.(row);
      }
    };
    if (!hasWin) {
      // No win — let idle animations play briefly, then diagonal cover
      const idleDuration =
        FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION / 1.5;

      // Guarantee minimum visible time even if browser batched the timer
      // (backgrounded tabs fire queued timeouts all at once on refocus)
      const elapsed = Date.now() - revealStartTime;
      const remainingDelay = Math.max(idleDuration, idleDuration - elapsed);

      await new Promise((r) => setTimeout(r, remainingDelay));

      // Wait for user to be on the tab before covering
      await waitForActive();

      const { allComplete } = coverAllSymbolsDiagonal(cleanupSymbol);
      await allComplete;

      // Check for retrigger (3+ scatters during free spins)
      const postCoverState = store.getState().featureGame;
      if (postCoverState.freeSpinTriggered) {
        // Show retrigger popup and wait for it to close
        dispatch(setShowAdditionalFreeSpins(true));
        await new Promise((resolve) => {
          const check = setInterval(() => {
            const s = store.getState();
            if (!s.animations.showAdditionalFreeSpins) {
              clearInterval(check);
              activeIntervalsRef.current = activeIntervalsRef.current.filter(id => id !== check);
              resolve();
            }
          }, 50);
          activeIntervalsRef.current.push(check);
        });
        // Reset trigger flag — retrigger has been processed
        dispatch(setFreeSpinTriggered(false));
      }

      // Check if this was the last round (uses updated freeSpinCount after retrigger)
      const postState = store.getState().featureGame;
      const isLastRound = postState.freeSpinIndex >= (postState.freeSpinCount || 15);

      if (isLastRound) {
        dispatch(setCriticalAnimationPlaying(true));
        dispatch(setShowFreeSpinOutro(true));
      }

      dispatch(setFreeSpinSpinning(false));
      spinningRef.current = false;

      return !isLastRound;
    } else {
      // Has win — let win controller show animations, then auto-cover
      spinningRef.current = false;
      dispatch(setFreeSpinComplete(true));

      // Wait for ALL win lines to play 1 full cycle
      await new Promise((resolve) => {
        const check = setInterval(() => {
          const state = store.getState();
          if (state.animations.freeSpinCycleComplete) {
            clearInterval(check);
            activeIntervalsRef.current = activeIntervalsRef.current.filter(id => id !== check);
            resolve();
          }
        }, 50);
        activeIntervalsRef.current.push(check);
      });

      // If animated win (big win, mega win, etc.), wait for celebration to finish
      const isAnimatedWin = roundData?.isAnimatedWin || false;
      if (isAnimatedWin) {
        await new Promise((resolve) => {
          const check = setInterval(() => {
            const state = store.getState();
            if (!state.animations.winAnimationsPlaying) {
              clearInterval(check);
              activeIntervalsRef.current = activeIntervalsRef.current.filter(id => id !== check);
              resolve();
            }
          }, 100);
          activeIntervalsRef.current.push(check);
        });
      }

      // Start diagonal cover — win effects stay visible during cover.
      // Each symbol's overlay gets cleaned up individually as its mask covers it.
      const { allComplete } = coverAllSymbolsDiagonal(cleanupSymbol);
      await allComplete;

      // All masks covered — now clean up remaining win effects (dim, lines, text)
      cleanupWinEffects();

      // Check for retrigger (3+ scatters during free spins)
      const postCoverState2 = store.getState().featureGame;
      if (postCoverState2.freeSpinTriggered) {
        // Show retrigger popup and wait for it to close
        dispatch(setShowAdditionalFreeSpins(true));
        await new Promise((resolve) => {
          const check = setInterval(() => {
            const s = store.getState();
            if (!s.animations.showAdditionalFreeSpins) {
              clearInterval(check);
              activeIntervalsRef.current = activeIntervalsRef.current.filter(id => id !== check);
              resolve();
            }
          }, 50);
          activeIntervalsRef.current.push(check);
        });
        // Reset trigger flag — retrigger has been processed
        dispatch(setFreeSpinTriggered(false));
      }

      // Check if this was the last round (uses updated freeSpinCount after retrigger)
      const postState = store.getState().featureGame;
      const isLastRound = postState.freeSpinIndex >= (postState.freeSpinCount || 15);

      if (isLastRound) {
        dispatch(setCriticalAnimationPlaying(true));
        dispatch(setShowFreeSpinOutro(true));
      }

      // Re-arm for next round
      dispatch(setFreeSpinComplete(false));
      dispatch(setFreeSpinSpinning(false));
      spinningRef.current = false;

      return !isLastRound;
    }
  }, [
    dispatch,
    revealAllSymbolsDiagonal,
    coverAllSymbolsDiagonal,
    cleanupWinEffects,
    freeSpinOverlayReelRefs,
    freeSpinReelRefs,
    waitForActive,
  ]);

  /**
   * Autoplay loop — runs handleFreeSpinSpin repeatedly until the last round.
   * Triggered automatically when free spins become active.
   */
  const startFreeSpinAutoplay = useCallback(async () => {
    let shouldContinue = true;

    while (shouldContinue) {
      // Safety: check free spins are still active
      const state = store.getState();
      if (!state.featureGame.freeSpinActive) break;

      shouldContinue = await handleFreeSpinSpin();
    }
  }, [handleFreeSpinSpin]);

  /**
   * Skip the win line cycle — user wants to cover early.
   * Dispatches freeSpinCycleComplete so the handleFreeSpinSpin await resolves.
   */
  const skipFreeSpinWinCycle = useCallback(() => {
    dispatch(setFreeSpinCycleComplete(true));
  }, [dispatch]);

  /**
   * Exit free spin mode: deactivate free spins, then diagonal reveal masks
   * to uncover the base game reels underneath. No symbol animations —
   * just the mask flip to smoothly transition back.
   */
  const exitFreeSpinReveal = useCallback(async () => {
    // 0. Reset local refs — ensure no leaked state from previous game
    spinningRef.current = false;
    clearPendingTimers();

    // 1. Reset ALL free spin state back to initial values (featureGame slice)
    dispatch(resetFreeSpinState());

    // 2. Reset animation slice states
    dispatch(setFreeSpinCycleComplete(false));
    dispatch(setFreeSpinCoverStarted(false));
    dispatch(setCounterAnimationStarted(false));
    dispatch(setCounterAnimationFinished(true));
    // NOTE: criticalAnimationPlaying stays TRUE — unlocked after reveal finishes

    // 3. Wait a frame for React to swap reel visibility
    await new Promise((r) => requestAnimationFrame(r));

    // 4. Trigger base game idle animations before reveal
    //    so spine animations are already running under the masks
    triggerInitialIdleAnimations();

    // 3. Diagonal reveal masks (no symbol animation callbacks)
    soundManager.play(SOUND_NAME.free_spin_reveal);
    const allAnimationPromises = [];

    for (let waveIndex = 0; waveIndex < DIAGONAL_WAVES.length; waveIndex++) {
      const wave = DIAGONAL_WAVES[waveIndex];
      const delay = waveIndex * DIAGONAL_REVEAL_DELAY;

      for (const { reel, row } of wave) {
        const promise = new Promise((resolveCell) => {
          const timeoutId = setTimeout(() => {
            const maskReel = maskReelRefs.current?.[reel];
            if (maskReel?.revealSymbolAt) {
              maskReel.revealSymbolAt(row, null, null).then(resolveCell);
            } else {
              resolveCell();
            }
          }, delay);
          cascadeTimeoutsRef.current.push(timeoutId);
        });
        allAnimationPromises.push(promise);
      }
    }

    await Promise.all(allAnimationPromises);

    // 6. All reveal animations done — sync balance with total win, unlock UI
    dispatch(syncBalance());
    dispatch(setSpinComplete(true));
    dispatch(setCriticalAnimationPlaying(false));
  }, [dispatch, maskReelRefs, triggerInitialIdleAnimations, clearPendingTimers]);

  // Cleanup all tracked timers on unmount
  useEffect(() => {
    return () => clearPendingTimers();
  }, [clearPendingTimers]);

  const contextValue = useMemo(
    () => ({
      coverAllSymbols,
      coverAllSymbolsDiagonal,
      revealAllSymbols,
      revealAllSymbolsDiagonal,
      startFreeSpinAutoplay,
      skipFreeSpinWinCycle,
      exitFreeSpinReveal,
    }),
    [
      coverAllSymbols,
      coverAllSymbolsDiagonal,
      revealAllSymbols,
      revealAllSymbolsDiagonal,
      startFreeSpinAutoplay,
      skipFreeSpinWinCycle,
      exitFreeSpinReveal,
    ],
  );

  return (
    <FreeSpinControlContext.Provider value={contextValue}>
      {children}
    </FreeSpinControlContext.Provider>
  );
};

const SAFE_DEFAULTS = {
  coverAllSymbols: () => Promise.resolve(),
  coverAllSymbolsDiagonal: () => ({
    firstCovered: Promise.resolve(),
    allComplete: Promise.resolve(),
  }),
  revealAllSymbols: () => Promise.resolve(),
  revealAllSymbolsDiagonal: () => ({
    lastRevealed: Promise.resolve(),
    allComplete: Promise.resolve(),
  }),
  startFreeSpinAutoplay: () => {},
  skipFreeSpinWinCycle: () => {},
  exitFreeSpinReveal: () => Promise.resolve(),
};

export const useFreeSpinControl = () => {
  const context = useContext(FreeSpinControlContext);
  return context || SAFE_DEFAULTS;
};
